/**
 * Light or dark, chosen by a person or left to their device.
 *
 * ## Why `system` is the absence of an attribute
 *
 * `tokens.ts` answers `prefers-color-scheme` on its own, so the device is already handled with no
 * JavaScript at all. This module exists only for the *override*: `data-theme="light"` or
 * `data-theme="dark"` on the root element, which the token file redefines under and which therefore
 * beats the media query in both directions.
 *
 * That is why `system` removes the attribute rather than resolving the media query and writing the
 * answer. Writing it would freeze the decision at page load, so somebody whose phone switches to dark
 * at sunset would stay light until they reloaded. Absent means "keep asking", which is what they meant.
 *
 * ## Storage is best-effort, deliberately
 *
 * The widget runs in a cross-site iframe. A browser partitioning storage does not return null from
 * `localStorage` - it throws a `SecurityError` on *access*, before any key is named. Losing a whole
 * exchange panel because somebody's browser has strict privacy settings would be an absurd trade for
 * remembering a colour, so every path here tolerates the throw and carries on.
 *
 * ## This is not a credential
 *
 * `frontend/ui/src/auth/session.ts` keeps its access token in memory precisely so this origin's storage
 * holds nothing that grants access to a document, and both rep surfaces assert their storage is empty
 * after signing in. A theme preference grants nothing, is written under a name that says what it is,
 * and is the only thing here that outlives the page.
 */

/** What somebody may choose. `system` is the default and means "do not override". */
export type ThemePreference = 'system' | 'light' | 'dark';

/** Namespaced, and named so that anybody auditing this origin's storage can see what it is. */
export const THEME_STORAGE_KEY = 'ddds.theme';

const OVERRIDES: readonly ThemePreference[] = ['light', 'dark'];

function isOverride(value: string | null): value is 'light' | 'dark' {
  return value !== null && (OVERRIDES as readonly string[]).includes(value);
}

/**
 * Put the choice on the document, or take the override off.
 *
 * The one place the attribute is written, so there is a single answer to "what makes this page dark".
 */
function stamp(preference: ThemePreference): void {
  const root = globalThis.document?.documentElement;
  if (root === undefined) return;

  if (preference === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', preference);
}

/**
 * What this person last chose, or `system` if they never did.
 *
 * An unrecognised stored value is treated as `system` rather than trusted: a key collision or a value
 * from some future version would otherwise be stamped into the attribute, match no rule, and render the
 * light palette while claiming to be something else.
 */
export function readThemePreference(): ThemePreference {
  try {
    const stored = globalThis.localStorage?.getItem(THEME_STORAGE_KEY) ?? null;
    return isOverride(stored) ? stored : 'system';
  } catch {
    return 'system';
  }
}

/**
 * Record a choice and apply it.
 *
 * The order matters: the theme is applied whether or not the write succeeds, because somebody pressed a
 * button and something has to happen on the screen they are looking at. A preference that will not
 * survive a reload is a much smaller failure than one that appears to do nothing.
 *
 * `system` clears the stored value rather than storing the word, so a borrowed device is left with
 * nothing of ours on it.
 */
export function setThemePreference(preference: ThemePreference): void {
  stamp(preference);

  try {
    if (preference === 'system') globalThis.localStorage?.removeItem(THEME_STORAGE_KEY);
    else globalThis.localStorage?.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // See the file comment: a partitioned or full store costs the memory of the choice, nothing more.
  }
}

/**
 * Apply the stored preference at boot, and report it so a control can render in the right state.
 *
 * Writes nothing on a first visit. Call it before mounting, so the first paint is already correct
 * rather than flashing the light palette at somebody who asked for dark.
 */
export function initTheme(): ThemePreference {
  const preference = readThemePreference();
  stamp(preference);
  return preference;
}

/**
 * Follow the host page's theme. The widget, and nothing else.
 *
 * An embedded panel takes its appearance from the application it is embedded in - two theme controls on
 * one screen, one of them ours, is worse than no control at all. `widgetInitMessageSchema` has carried
 * an optional `theme` since stage 8 for this, and section 14 governs the message.
 *
 * Nothing is persisted, because the choice belongs to the host page rather than to this person: a rep
 * who opens the standalone application later should get their own preference, not the CRM's.
 * `undefined` means the host said nothing, which falls back to the device.
 */
export function applyHostTheme(theme: 'light' | 'dark' | undefined): void {
  stamp(theme ?? 'system');
}
